import { useRef, type FC } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment } from 'react-relay';
import { BarHeader } from 'dibs-elements/exports/BarHeader.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile.tsx';
import { QuickViewProvider } from 'dibs-buyer-product-tile/exports/QuickView.ts';
import { useSharedUrgencySignals } from '../hooks/sharedUrgencySignals/useSharedUrgencySignals.ts';
import { useItemShippingPrices } from '../hooks/useItemShippingPrices.ts';
import { useSbSharedItemTracking } from '../hooks/sharedItemTracking/useSbSharedItemTracking.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

import { type SbSharedRelatedItems_itemSearch$key } from './__generated__/SbSharedRelatedItems_itemSearch.graphql.ts';
import { type SbSharedRelatedItems_viewer$key } from './__generated__/SbSharedRelatedItems_viewer.graphql.ts';

import styles from './SbSharedRelatedItems.scss';

type Props = {
    viewer: SbSharedRelatedItems_viewer$key;
    itemSearch: SbSharedRelatedItems_itemSearch$key;
};

export const SbSharedRelatedItems: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
}) => {
    const gridContainerRef = useRef<HTMLDivElement | null>(null);
    const viewer = useFragment(
        graphql`
            fragment SbSharedRelatedItems_viewer on Viewer {
                ...SharedItemTile_viewer
                ...useSbSharedItemTracking_viewer
            }
        `,
        viewerRef
    );
    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRelatedItems_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                ...useSbSharedItemTracking_itemSearch
                relatedItems: youMayAlsoLike(first: 12) {
                    edges {
                        node {
                            item {
                                serviceId
                                ...SharedItemTile_item
                                    @arguments(
                                        isTrade: $isTrade
                                        priceBookName: $priceBookName
                                        showSeller: $showSeller
                                    )
                                ...useSharedUrgencySignals_item
                                ...useItemShippingPrices_item
                                ...useSbSharedItemTracking_item
                                ...shippingCostHelpers_item
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );
    const relatedItems = (itemSearch?.relatedItems?.edges || [])
        .map(item => item?.node?.item || null)
        .filter(filterFalsy);

    const { fetchItemUrgencySignals, getUrgencySignal } = useSharedUrgencySignals(relatedItems);
    const { fetchItemShippingPrices, getItemShippingPrices, getItemShippingTrackingData } =
        useItemShippingPrices(relatedItems);
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch,
        items: relatedItems,
        pageType: 'related',
        getUrgencySignal,
        getItemShippingPrices,
        getItemShippingTrackingData,
    });

    const { isQuickFiltersRedesignVariant } = useServerVarsContext();

    const title = (
        <FormattedMessage id="sb.SbSharedRelatedItems.title" defaultMessage="Related Items" />
    );

    if (relatedItems.length > 0) {
        return (
            <>
                <BarHeader title={title} />
                <QuickViewProvider elementRef={gridContainerRef}>
                    <div
                        className={classnames(styles.grid, {
                            [styles.isQuickFiltersRedesignVariant]: isQuickFiltersRedesignVariant,
                        })}
                        data-tn="related-items"
                        ref={gridContainerRef}
                    >
                        {relatedItems.map((relatedItem, index) => {
                            if (relatedItem) {
                                return (
                                    <SharedItemTile
                                        user={null}
                                        key={index}
                                        index={index}
                                        item={relatedItem}
                                        viewer={viewer}
                                        itemSearch={itemSearch}
                                        beforeRender={() => {
                                            fetchItemUrgencySignals(index);
                                            fetchItemShippingPrices(index);
                                        }}
                                        onContentVisible={() => {
                                            fireItemImpressionTracking({
                                                itemId: relatedItem.serviceId,
                                                index,
                                            });
                                        }}
                                        onClick={() =>
                                            fireItemClickTracking({
                                                itemId: relatedItem.serviceId,
                                                index,
                                            })
                                        }
                                        urgencySignal={
                                            getUrgencySignal(relatedItem.serviceId)?.message
                                        }
                                        shippingPrices={getItemShippingPrices(
                                            relatedItem.serviceId
                                        )}
                                    />
                                );
                            } else {
                                return null;
                            }
                        })}
                    </div>
                </QuickViewProvider>
            </>
        );
    }
    return null;
};
